<template>
  <div
    v-if="user"
    class="flex overflow-hidden justify-center items-center rounded-full user-avatar-container shrink-0"
    :style="avatarContainerStyle">
    <img
      v-if="user.avatar_url"
      :src="getAvatarUrlForSize()"
      alt="User avatar"
      class="object-cover w-full h-full" />

    <div
      v-else
      class="flex relative justify-center items-center w-full h-full font-semibold text-white select-none"
      :style="[getAvatarStyle, { fontSize: fontSize }]">
      <span style="line-height: normal">{{ getAvatarLetters }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL

const props = defineProps({
  user: Object,
  size: {
    type: [String, Number],
    default: 'md',
    validator: (value) => {
      if (typeof value === 'number') return value > 0
      // Support predefined sizes or Tailwind size patterns (e.g., "8", "12", "size-8")
      // Also support 'auto' to let container control sizing
      const str = String(value)
      if (str === 'auto') return true
      if (/^(xs|sm|md|lg|xl|2xl|3xl)$/.test(str)) return true
      if (/^(\d+|size-\d+)$/.test(str)) return true
      return false
    }
  }
})

// Size mapping for predefined sizes (in pixels)
const sizeMap = {
  xs: 16, // 4 * 4px
  sm: 24, // 6 * 4px
  md: 32, // 8 * 4px (default)
  lg: 48, // 12 * 4px
  xl: 64, // 16 * 4px
  '2xl': 96, // 24 * 4px
  '3xl': 128 // 32 * 4px
}

// Calculate the actual pixel size
const pixelSize = computed(() => {
  // If size is 'auto', return 0 (will be handled by container style)
  if (props.size === 'auto') {
    return 0
  }

  if (typeof props.size === 'number') {
    return props.size
  }

  const str = String(props.size)

  // Handle predefined sizes
  if (sizeMap[str]) {
    return sizeMap[str]
  }

  // Handle Tailwind size patterns like "8" or "size-8"
  const match = str.match(/(?:size-)?(\d+)/)
  if (match) {
    const num = parseInt(match[1], 10)
    // Tailwind sizes are in rem units, where 1 = 0.25rem = 4px
    return num * 4
  }

  // Default fallback
  return sizeMap.md
})

// Container style with fixed dimensions to ensure circular shape
const avatarContainerStyle = computed(() => {
  // If size is 'auto', let the container control the sizing
  if (props.size === 'auto') {
    return {
      width: '100%',
      height: '100%',
      flexShrink: '0',
      flexGrow: '0',
      aspectRatio: '1 / 1',
      boxSizing: 'border-box'
    }
  }

  const size = `${pixelSize.value}px`
  return {
    '--avatar-size': size,
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
    maxWidth: size,
    maxHeight: size,
    flexShrink: '0',
    flexGrow: '0',
    aspectRatio: '1 / 1',
    boxSizing: 'border-box'
  }
})

// Font size scales with avatar size
const fontSize = computed(() => {
  const size = pixelSize.value
  if (size <= 20) return '0.625rem' // 10px
  if (size <= 32) return '0.75rem' // 12px
  if (size <= 48) return '0.875rem' // 14px
  if (size <= 64) return '1rem' // 16px
  if (size <= 96) return '1.25rem' // 20px
  return '1.5rem' // 24px
})

// Get avatar initials (1-2 characters)
const getAvatarLetters = computed(() => {
  const name =
    props.user?.displayname || props.user?.username || props.user?.display_name
  if (!name) return 'U'

  // For small avatars (≤ 24px), use only 1 character
  if (pixelSize.value <= 24) {
    return name.charAt(0).toUpperCase()
  }

  // Get initials from display name (first letter of first 2 words)
  const words = name.trim().split(/\s+/)
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase()
  }
  // For single word, use first 2 characters
  return name.slice(0, 2).toUpperCase()
})

// Color palette for fallback avatars
const avatarColors = [
  '#3498DB', // blue
  '#2ECC71', // green
  '#9B59B6', // purple
  '#F39C12', // orange
  '#E74C3C', // red
  '#1ABC9C', // teal
  '#E91E63', // pink
  '#00BCD4', // cyan
  '#FF5722', // deep orange
  '#673AB7' // deep purple
]

// Get background style based on username/id hash
const getAvatarStyle = computed(() => {
  // Generate a consistent color based on user identifier
  const identifier =
    props.user?.username || props.user?.user_id || props.user?.id || 'default'
  let hash = 0
  for (let i = 0; i < identifier.length; i++) {
    hash = identifier.charCodeAt(i) + ((hash << 5) - hash)
  }
  const colorIndex = Math.abs(hash) % avatarColors.length
  return {
    backgroundColor: avatarColors[colorIndex]
  }
})

function getAvatarUrl(path) {
  if (!path) return null

  // If path is already a full URL, return it as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path
  }

  const bucketName = 'avatars'
  return `${supabaseUrl}/storage/v1/object/public/${bucketName}/${path}`
}

// Get the appropriate avatar URL based on size
function getAvatarUrlForSize() {
  const size = pixelSize.value

  // Use small version for very small avatars (xs, sm)
  if (size <= 32 && props.user?.avatar_url_sm) {
    return getAvatarUrl(props.user.avatar_url_sm)
  }

  // Use regular version for medium and larger avatars
  return getAvatarUrl(props.user.avatar_url)
}
</script>

<style scoped>
/* Ensure avatar maintains circular shape regardless of parent constraints */
.user-avatar-container {
  flex-shrink: 0 !important;
  flex-grow: 0 !important;
  aspect-ratio: 1 / 1 !important;
  width: var(--avatar-size) !important;
  height: var(--avatar-size) !important;
  min-width: var(--avatar-size) !important;
  min-height: var(--avatar-size) !important;
  max-width: var(--avatar-size) !important;
  max-height: var(--avatar-size) !important;
}
</style>
